// One proposed change in the conversation (SCR-311's change card; SCR-312 behind Review). The
// operation in words and its target by name, its status, the diff by path and its expiry; Review
// opens the risks, their confirmations and Approve / Reject (change-review.tsx). Once decided the
// card is read-only: applied says whether the server verified it (a read-back digest; the wire
// carries no read-back values, so the card shows the reviewed diff as "What changed") and who
// approved it, failed the code and what to do, rejected the reason, expired that it lapsed. In an
// ended conversation every card is read-only. No id is shown (AS-1).
import { useEffect, useState } from "preact/hooks";
import { Chip, type ChipTone } from "../../primitives/page";
import { ChangeReview, DiffTable, ProblemBanner } from "./change-review";
import { approvalProblem, CHANGE_STATUS_LABELS, effectiveStatus, expiryText, type AgentChange, type AgentChangeStatus } from "./confirmations";
import { when, WITHHELD_TEXT, withoutIds } from "./model";

/** How often an open proposal's countdown is redrawn. */
export const EXPIRY_TICK_MS = 15_000;

const STATUS_TONES: Readonly<Record<AgentChangeStatus, ChipTone>> = {
  proposed: "amber",
  approved: "blue",
  applying: "blue",
  applied: "green",
  failed: "red",
  rejected: "grey",
  expired: "grey",
};

export interface ChangeCardProps {
  sessionId: string;
  change: AgentChange;
  onChanged(change: AgentChange): void;
  /**
   * Whether the conversation has ended: its cards are read-only (FLOW-026, "an ended session
   * opens read-only"), and a pending proposal reads as lapsed with no Approve or Reject.
   */
  ended?: boolean | undefined;
  /** The clock, for a test. */
  now?: (() => number) | undefined;
}

export function ChangeCard({ sessionId, change, onChanged, ended = false, now = Date.now }: ChangeCardProps) {
  const [nowMs, setNowMs] = useState(now);
  const [reviewing, setReviewing] = useState(false);
  const status = effectiveStatus(change, nowMs, ended);
  const open = status === "proposed";
  const endedWhilePending = ended && change.status === "proposed";
  // B3: the diff is withheld from this reader, so they cannot review what they would approve.
  const reviewable = open && !change.withheld;

  useEffect(() => {
    if (!open) return;
    const timer = setInterval(() => setNowMs(now()), EXPIRY_TICK_MS);
    return () => clearInterval(timer);
  }, [open, now]);

  const summary = withoutIds(change.summary);
  return (
    <article class={`card assistant-change assistant-change-${status}`} aria-label={`Proposed change: ${summary}`} data-status={status}>
      <header class="assistant-change-header">
        <p class="assistant-change-summary">
          <strong>{summary}</strong>
        </p>
        <Chip tone={STATUS_TONES[status]}>{CHANGE_STATUS_LABELS[status]}</Chip>
      </header>
      {change.targetName !== null && <p class="assistant-change-target">On {withoutIds(change.targetName)}</p>}
      {endedWhilePending ? (
        <p class="assistant-change-outcome" role="status">
          This conversation has ended, so this change can no longer be approved or rejected and nothing was written. Start a new conversation and ask the
          Assistant to propose it again if it is still needed.
        </p>
      ) : (
        <Outcome change={change} status={status} />
      )}
      {change.withheld ? (
        <p class="assistant-change-withheld muted">{WITHHELD_TEXT}</p>
      ) : (
        status !== "applied" && <DiffTable diff={change.diff} />
      )}
      {open && change.withheld && (
        <p class="assistant-change-expiry">{expiryText(change, nowMs)} · Only the person who started this conversation or an AllCode administrator can review and approve it.</p>
      )}
      {reviewable && (
        <>
          <p class="assistant-change-expiry">
            {expiryText(change, nowMs)}
            {change.flags.length > 0 && ` · ${change.flags.length === 1 ? "1 risk" : `${change.flags.length} risks`} to confirm`}
          </p>
          {reviewing ? (
            <ChangeReview sessionId={sessionId} change={change} onChanged={onChanged} />
          ) : (
            <div class="assistant-change-actions">
              <button type="button" class="button button-primary" onClick={() => setReviewing(true)}>
                Review
              </button>
            </div>
          )}
        </>
      )}
    </article>
  );
}

/** The failure code whose write committed (only its read-back failed), so the card never says "Not applied". */
const WRITE_KEPT = "readback_failed";

/** What became of a decided change. */
function Outcome({ change, status }: { change: AgentChange; status: AgentChangeStatus }) {
  const by = change.approvedByName === null ? "" : ` by ${withoutIds(change.approvedByName)}`;
  const at = change.approvedAt === null ? "" : ` on ${when(change.approvedAt)}`;
  switch (status) {
    case "applied":
      return (
        <>
          <p class="assistant-change-outcome" role="status">
            {change.verifiedDigest === null ? "Applied" : "Applied and verified"}
            {by}
            {at}.{change.withheld ? "" : change.verifiedDigest === null ? " It has not been verified yet. What changed:" : " What changed:"}
          </p>
          {!change.withheld && <DiffTable diff={change.diff} applied />}
        </>
      );
    case "approved":
    case "applying":
      return (
        <p class="assistant-change-outcome" role="status">
          Approved{by}; applying now.
        </p>
      );
    case "failed": {
      const code = change.errorCode ?? "failed";
      const problem = approvalProblem(code);
      return (
        <ProblemBanner
          problem={{ ...problem, title: code === WRITE_KEPT ? problem.title : `Not applied: ${problem.title.toLowerCase()}` }}
          action={
            <p class="muted">
              Code <code>{withoutIds(code)}</code>
            </p>
          }
        />
      );
    }
    case "rejected":
      return (
        <p class="assistant-change-outcome">
          Rejected{change.rejectReason === null ? "." : `: “${withoutIds(change.rejectReason)}”`}
        </p>
      );
    case "expired":
      return <p class="assistant-change-outcome">This proposal lapsed after an hour without a decision. Ask the Assistant to propose it again if it is still needed.</p>;
    case "proposed":
      return null;
  }
}
